import { useState } from "react";
import { Plus, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAliases, useAliasMutations } from "@/hooks/useSparePartTypes";

const SOURCES = ["partsnl", "beekman", "miele_msd", "bosch", "ocr", "user"];
const LANGS = ["en", "nl", "de", "fr"];

export default function AliasChipEditor({ partTypeId }: { partTypeId: string }) {
  const { data: aliases = [] } = useAliases(partTypeId);
  const { add, remove } = useAliasMutations(partTypeId);
  const [open, setOpen] = useState(false);
  const [alias, setAlias] = useState("");
  const [language, setLanguage] = useState<string>("");
  const [source, setSource] = useState<string>("");
  const [brand, setBrand] = useState("");

  const handleAdd = async () => {
    if (!alias.trim()) return;
    await add.mutateAsync({
      part_type_id: partTypeId,
      alias: alias.trim(),
      language: language || null,
      source: source || null,
      brand: brand.trim() || null,
    });
    setAlias(""); setBrand(""); setLanguage(""); setSource("");
    setOpen(false);
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {aliases.map((a) => (
        <Badge key={a.id} variant="outline" className="gap-1 pr-1 text-xs font-normal">
          <span>{a.alias}</span>
          {a.brand && <span className="text-muted-foreground">·{a.brand}</span>}
          {a.language && <span className="text-muted-foreground">·{a.language}</span>}
          {a.source && <span className="text-muted-foreground">·{a.source}</span>}
          <button
            type="button"
            onClick={() => remove.mutate(a.id)}
            className="hover:bg-muted rounded-sm p-0.5"
            aria-label={`Remove alias ${a.alias}`}
          >
            <X className="w-3 h-3" />
          </button>
        </Badge>
      ))}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button size="sm" variant="outline" className="h-6 gap-1 text-xs">
            <Plus className="w-3 h-3" /> Alias
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-3 space-y-2" align="start">
          <Input placeholder="Alias text *" value={alias} onChange={(e) => setAlias(e.target.value)} className="h-8 text-sm" />
          <div className="grid grid-cols-2 gap-2">
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Language" /></SelectTrigger>
              <SelectContent>{LANGS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Source" /></SelectTrigger>
              <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Input placeholder="Brand (optional, scopes alias)" value={brand} onChange={(e) => setBrand(e.target.value)} className="h-8 text-sm" />
          <Button size="sm" className="w-full" onClick={handleAdd} disabled={!alias.trim()}>Add alias</Button>
        </PopoverContent>
      </Popover>
    </div>
  );
}
